import React, { useEffect, useState } from "react";
import useSalonStore from "./../../store/salonStore";
import {
  FiEdit3,
  FiSave,
  FiX,
  FiClock,
  FiCalendar,
  FiPauseCircle,
  FiUsers,
  FiUser,
  FiWatch,
  FiMapPin,
  FiInfo,
  FiPlus,
  FiTrash2,
} from "react-icons/fi";

function formatFrenchDateCapitalized(isoDate) {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  const formatted = new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

function formatTimeForDisplay(timeString) {
  if (!timeString) return "";
  return timeString.includes(":") ? timeString.slice(0, 5) : timeString;
}

function formatTimeForInput(timeString) {
  if (!timeString) return "";
  return timeString.includes(":") ? timeString.slice(0, 5) : timeString;
}

function formatSpecialDate(dateString) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString + "T00:00:00");
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date);
  } catch {
    return dateString;
  }
}

// Composant pour l'input de date avec calendrier
const DatePicker = ({ value, onChange, placeholder }) => {
  return (
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-nunito text-[14px]"
      placeholder={placeholder}
      min={new Date().toISOString().split("T")[0]} // Empêcher les dates passées
    />
  );
};

export default function SalonPage() {
  const { currentSalon, isLoadingSalon, getSalonById, updateSalon } =
    useSalonStore();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    designation: "",
    openingTime: "",
    closingTime: "",
    maxDailyBookings: 0,
    maxHourlyBookings: 0,
    slotDuration: 0,
    breakStart: "",
    breakEnd: "",
    closedDays: [],
  });

  const [specificDate, setSpecificDate] = useState("");

  useEffect(() => {
    getSalonById(1);
  }, []);

  useEffect(() => {
    if (currentSalon) {
      setFormData({
        designation: currentSalon.designation || "",
        openingTime: formatTimeForInput(currentSalon.openingTime) || "",
        closingTime: formatTimeForInput(currentSalon.closingTime) || "",
        maxDailyBookings: currentSalon.maxDailyBookings || 0,
        maxHourlyBookings: currentSalon.maxHourlyBookings || 0,
        slotDuration: currentSalon.slotDuration || 0,
        breakStart: formatTimeForInput(currentSalon.breakStart) || "",
        breakEnd: formatTimeForInput(currentSalon.breakEnd) || "",
        closedDays: currentSalon.closedDays || [],
      });
    }
  }, [currentSalon]);

  const handleEditToggle = () => {
    if (isEditing) {
      setFormData({
        designation: currentSalon.designation || "",
        openingTime: formatTimeForInput(currentSalon.openingTime) || "",
        closingTime: formatTimeForInput(currentSalon.closingTime) || "",
        maxDailyBookings: currentSalon.maxDailyBookings || 0,
        maxHourlyBookings: currentSalon.maxHourlyBookings || 0,
        slotDuration: currentSalon.slotDuration || 0,
        breakStart: formatTimeForInput(currentSalon.breakStart) || "",
        breakEnd: formatTimeForInput(currentSalon.breakEnd) || "",
        closedDays: currentSalon.closedDays || [],
      });
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    try {
      const updateData = {
        designation: formData.designation,
        openingTime: formData.openingTime,
        closingTime: formData.closingTime,
        maxDailyBookings: formData.maxDailyBookings,
        maxHourlyBookings: formData.maxHourlyBookings,
        slotDuration: formData.slotDuration,
        closedDays: formData.closedDays,
      };

      await updateSalon(1, updateData);
      setIsEditing(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour du salon:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNumberInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: parseInt(value) || 0,
    }));
  };

  const handleClosedDaysChange = (days) => {
    setFormData((prev) => ({
      ...prev,
      closedDays: days,
    }));
  };

  const toggleClosedDay = (day) => {
    const currentDays = formData.closedDays || [];
    console.log({ day, currentDays });
    if (currentDays.includes(day) || currentDays.includes(day)) {
      handleClosedDaysChange(currentDays.filter((d) => d !== day));
    } else {
      handleClosedDaysChange([...currentDays, day]);
    }
  };

  const addSpecificDate = () => {
    if (specificDate) {
      // Formater la date en YYYY-MM-DD
      const formattedDate = specificDate;

      // Vérifier si la date est déjà dans la liste
      if (!formData.closedDays.includes(formattedDate)) {
        handleClosedDaysChange([...formData.closedDays, formattedDate]);
        setSpecificDate("");
      } else {
        alert("Cette date est déjà dans la liste des jours fermés");
      }
    }
  };

  const removeClosedDay = (dayToRemove) => {
    handleClosedDaysChange(
      formData.closedDays.filter((day) => day !== dayToRemove)
    );
  };

  const daysOfWeek = [
    { value: "monday", label: "Lundi", french: "lundi" },
    { value: "tuesday", label: "Mardi", french: "mardi" },
    { value: "wednesday", label: "Mercredi", french: "mercredi" },
    { value: "thursday", label: "Jeudi", french: "jeudi" },
    { value: "friday", label: "Vendredi", french: "vendredi" },
    { value: "saturday", label: "Samedi", french: "samedi" },
    { value: "sunday", label: "Dimanche", french: "dimanche" },
  ];

  const getDayLabel = (dayValue) => {
    const day = daysOfWeek.find(
      (d) => d.value === dayValue || d.french === dayValue
    );
    if (day) return day.label;

    // Si c'est une date spéciale, la formater
    if (dayValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return formatSpecialDate(dayValue);
    }

    return dayValue;
  };

  const isSpecificDate = (day) => {
    return day.match(/^\d{4}-\d{2}-\d{2}$/);
  };

  const isWeekDay = (day) => {
    return daysOfWeek.some((d) => d.value === day || d.french === day);
  };

  // Séparer les jours de fermeture par type
  const closedWeekDays = formData.closedDays.filter((day) => isWeekDay(day));
  const closedSpecialDates = formData.closedDays.filter((day) =>
    isSpecificDate(day)
  );

  if (isLoadingSalon) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin h-8 w-8 border-b-2 border-gray-900"></div>
        <span className="ml-3 font-nunito text-[14px] text-text">
          Chargement du salon...
        </span>
      </div>
    );
  }

  if (!currentSalon) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <FiMapPin size={48} className="mx-auto" />
        </div>
        <h3 className="font-poppins font-semibold text-[18px] text-title mb-2">
          Aucun salon trouvé
        </h3>
        <p className="font-nunito text-[14px] text-text">
          Le salon n'a pas encore été configuré.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* En-tête principal */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-6 gap-4">
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
            <h2 className="font-poppins font-semibold text-[18px] text-title">
              Mon Salon
            </h2>
            <span
              className={`px-3 py-1 font-nunito text-[14px] font-semibold text-action flex items-center gap-1 ${
                currentSalon.status
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              <FiInfo size={14} />
              {currentSalon.status ? "Actif" : "Inactif"}
            </span>
          </div>
          <p className="font-nunito text-[14px] text-text">
            Gérez les paramètres de votre salon. Modifiez les horaires, les
            créneaux et les jours de fermeture.
          </p>
        </div>

        <div className="flex gap-3 w-full lg:w-auto">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                className="flex items-center justify-center gap-2 font-nunito text-[14px] font-semibold text-action px-4 py-2 bg-green-600 text-white font-medium hover:bg-green-700 transition-colors rounded-lg shadow-sm"
              >
                <FiSave size={16} />
                Enregistrer
              </button>
              <button
                onClick={handleEditToggle}
                className="flex items-center justify-center gap-2 font-nunito text-[14px] font-semibold text-action px-4 py-2 bg-gray-500 text-white font-medium hover:bg-gray-600 transition-colors rounded-lg shadow-sm"
              >
                <FiX size={16} />
                Annuler
              </button>
            </>
          ) : (
            <button
              onClick={handleEditToggle}
              className="flex items-center justify-center gap-2 font-nunito text-[14px] font-semibold text-action px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium hover:from-blue-700 hover:to-purple-700 transition-colors rounded-lg shadow-sm"
            >
              <FiEdit3 size={16} />
              Modifier le salon
            </button>
          )}
        </div>
      </div>

      {/* Carte du salon */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 px-6 py-4 bg-gradient-to-r from-gray-50 to-white">
          <h3 className="font-poppins font-semibold text-[18px] text-title flex items-center gap-2">
            <FiMapPin className="text-blue-600" />
            Paramètres du Salon
          </h3>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Colonne gauche - Informations générales */}
            <div className="space-y-6">
              {/* Désignation */}
              <div>
                <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2 flex items-center gap-2">
                  <FiMapPin size={16} className="text-gray-400" />
                  Nom du salon
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.designation}
                    onChange={(e) =>
                      handleInputChange("designation", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-nunito text-[14px]"
                    placeholder="Nom de votre salon"
                  />
                ) : (
                  <p className="font-poppins font-semibold text-[18px] text-title bg-gray-50 px-3 py-2 rounded-lg">
                    {currentSalon.designation}
                  </p>
                )}
              </div>

              {/* Horaires d'ouverture */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2 flex items-center gap-2">
                    <FiClock size={16} className="text-gray-400" />
                    Ouverture
                  </label>
                  {isEditing ? (
                    <input
                      type="time"
                      value={formData.openingTime}
                      onChange={(e) =>
                        handleInputChange("openingTime", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-nunito text-[14px]"
                    />
                  ) : (
                    <div className="flex items-center gap-2 font-poppins font-semibold text-[18px] text-title bg-gray-50 px-3 py-2 rounded-lg">
                      <FiClock className="text-blue-600" />
                      {formatTimeForDisplay(currentSalon.openingTime)}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2 flex items-center gap-2">
                    <FiClock size={16} className="text-gray-400" />
                    Fermeture
                  </label>
                  {isEditing ? (
                    <input
                      type="time"
                      value={formData.closingTime}
                      onChange={(e) =>
                        handleInputChange("closingTime", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-nunito text-[14px]"
                    />
                  ) : (
                    <div className="flex items-center gap-2 font-poppins font-semibold text-[18px] text-title bg-gray-50 px-3 py-2 rounded-lg">
                      <FiClock className="text-blue-600" />
                      {formatTimeForDisplay(currentSalon.closingTime)}
                    </div>
                  )}
                </div>
              </div>

              {/* Pause déjeuner */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2 flex items-center gap-2">
                    <FiPauseCircle size={16} className="text-gray-400" />
                    Début pause
                  </label>
                  {isEditing ? (
                    <input
                      type="time"
                      value={formData.breakStart}
                      onChange={(e) =>
                        handleInputChange("breakStart", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-nunito text-[14px]"
                    />
                  ) : (
                    <div className="flex items-center gap-2 font-poppins font-semibold text-[18px] text-title bg-gray-50 px-3 py-2 rounded-lg">
                      <FiPauseCircle className="text-orange-500" />
                      {currentSalon.breakStart
                        ? formatTimeForDisplay(currentSalon.breakStart)
                        : "Non définie"}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2 flex items-center gap-2">
                    <FiPauseCircle size={16} className="text-gray-400" />
                    Fin pause
                  </label>
                  {isEditing ? (
                    <input
                      type="time"
                      value={formData.breakEnd}
                      onChange={(e) =>
                        handleInputChange("breakEnd", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-nunito text-[14px]"
                    />
                  ) : (
                    <div className="flex items-center gap-2 font-poppins font-semibold text-[18px] text-title bg-gray-50 px-3 py-2 rounded-lg">
                      <FiPauseCircle className="text-orange-500" />
                      {currentSalon.breakEnd
                        ? formatTimeForDisplay(currentSalon.breakEnd)
                        : "Non définie"}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Colonne droite - Capacités et fermetures */}
            <div className="space-y-6">
              {/* Capacités de réservation */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2 flex items-center gap-2">
                    <FiUsers size={16} className="text-gray-400" />
                    Réservations/jour
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      value={formData.maxDailyBookings}
                      onChange={(e) =>
                        handleNumberInputChange(
                          "maxDailyBookings",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-nunito text-[14px]"
                      min="1"
                    />
                  ) : (
                    <p className="font-poppins font-semibold text-[18px] text-title bg-gray-50 px-3 py-2 rounded-lg">
                      {currentSalon.maxDailyBookings}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2 flex items-center gap-2">
                    <FiUser size={16} className="text-gray-400" />
                    Réservations/heure
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      value={formData.maxHourlyBookings}
                      onChange={(e) =>
                        handleNumberInputChange(
                          "maxHourlyBookings",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-nunito text-[14px]"
                      min="1"
                    />
                  ) : (
                    <p className="font-poppins font-semibold text-[18px] text-title bg-gray-50 px-3 py-2 rounded-lg">
                      {currentSalon.maxHourlyBookings}
                    </p>
                  )}
                </div>
              </div>

              {/* Durée des créneaux */}
              <div>
                <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2 flex items-center gap-2">
                  <FiWatch size={16} className="text-gray-400" />
                  Durée des créneaux
                </label>
                {isEditing ? (
                  <input
                    type="number"
                    value={formData.slotDuration}
                    onChange={(e) =>
                      handleNumberInputChange("slotDuration", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-nunito text-[14px]"
                    min="15"
                    step="15"
                  />
                ) : (
                  <p className="font-poppins font-semibold text-[18px] text-title bg-gray-50 px-3 py-2 rounded-lg">
                    {currentSalon.slotDuration} minutes
                  </p>
                )}
              </div>

              {/* Jours de fermeture */}
              <div>
                <label className="block font-poppins font-medium text-[15px] text-subtitle mb-3 flex items-center gap-2">
                  <FiCalendar size={16} className="text-gray-400" />
                  Jours de fermeture
                </label>
                {isEditing ? (
                  <div className="space-y-4">
                    {/* Jours de la semaine */}
                    <div>
                      <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                        Jours de la semaine
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {daysOfWeek.map((day) => (
                          <label
                            key={day.value}
                            className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={
                                formData.closedDays?.includes(day.value) ||
                                formData.closedDays?.includes(day.french)
                              }
                              onChange={() => toggleClosedDay(day.value)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="font-nunito text-[14px] text-text">
                              {day.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Dates spéciales */}
                    <div className="border-t pt-4">
                      <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                        Dates spéciales de fermeture
                      </label>
                      <div className="flex gap-2 mb-3">
                        <DatePicker
                          value={specificDate}
                          onChange={setSpecificDate}
                          placeholder="Choisir une date"
                        />
                        <button
                          type="button"
                          onClick={addSpecificDate}
                          disabled={!specificDate}
                          className="px-3 py-2 bg-blue-600 text-white font-nunito text-[14px] font-semibold text-action rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                        >
                          <FiPlus size={14} />
                          Ajouter
                        </button>
                      </div>

                      {/* Liste des jours fermés */}
                      {(closedWeekDays.length > 0 ||
                        closedSpecialDates.length > 0) && (
                        <div className="space-y-2">
                          {closedWeekDays.length > 0 && (
                            <div>
                              <span className="font-nunito text-[14px] text-text mb-1 block">
                                Jours récurrents:
                              </span>
                              <div className="flex flex-wrap gap-2">
                                {closedWeekDays.map((day, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 font-nunito text-[14px] border border-blue-200"
                                  >
                                    {getDayLabel(day)}
                                    <button
                                      type="button"
                                      onClick={() => removeClosedDay(day)}
                                      className="text-blue-600 hover:text-red-600 transition-colors"
                                    >
                                      <FiTrash2 size={12} />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {closedSpecialDates.length > 0 && (
                            <div>
                              <span className="font-nunito text-[14px] text-text mb-1 block">
                                Dates spéciales:
                              </span>
                              <div className="flex flex-wrap gap-2">
                                {closedSpecialDates.map((date, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 font-nunito text-[14px] border border-purple-200"
                                  >
                                    {getDayLabel(date)}
                                    <button
                                      type="button"
                                      onClick={() => removeClosedDay(date)}
                                      className="text-purple-600 hover:text-red-600 transition-colors"
                                    >
                                      <FiTrash2 size={12} />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {currentSalon.closedDays &&
                    currentSalon.closedDays.length > 0 ? (
                      <>
                        {/* Jours récurrents */}
                        {currentSalon.closedDays.filter((day) => isWeekDay(day))
                          .length > 0 && (
                          <div>
                            <span className="font-nunito text-[14px] text-text mb-2 block">
                              Jours récurrents:
                            </span>
                            <div className="space-y-2">
                              {currentSalon.closedDays
                                .filter((day) => isWeekDay(day))
                                .map((day, index) => (
                                  <div
                                    key={index}
                                    className="flex items-center gap-2 font-nunito text-[14px] bg-blue-50 px-3 py-2 rounded-lg"
                                  >
                                    <FiCalendar className="text-blue-600" />
                                    <span className="text-blue-800 font-medium">
                                      {getDayLabel(day)}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}

                        {/* Dates spéciales */}
                        {currentSalon.closedDays.filter((day) =>
                          isSpecificDate(day)
                        ).length > 0 && (
                          <div>
                            <span className="font-nunito text-[14px] text-text mb-2 block">
                              Dates spéciales:
                            </span>
                            <div className="space-y-2">
                              {currentSalon.closedDays
                                .filter((day) => isSpecificDate(day))
                                .map((date, index) => (
                                  <div
                                    key={index}
                                    className="flex items-center gap-2 font-nunito text-[14px] bg-purple-50 px-3 py-2 rounded-lg"
                                  >
                                    <FiCalendar className="text-purple-600" />
                                    <span className="text-purple-800 font-medium">
                                      {getDayLabel(date)}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="font-nunito text-[14px] text-text bg-gray-50 px-3 py-2 rounded-lg">
                        Aucun jour de fermeture configuré
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Résumé des horaires */}
          {!isEditing && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h4 className="font-poppins font-semibold text-[18px] text-title mb-3 flex items-center gap-2">
                <FiClock className="text-blue-600" />
                Horaires de travail
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 font-nunito text-[14px]">
                {daysOfWeek.map((day) => {
                  const isClosed =
                    currentSalon.closedDays?.includes(day.value) ||
                    currentSalon.closedDays?.includes(day.french);
                  const hasBreak =
                    currentSalon.breakStart && currentSalon.breakEnd;

                  return (
                    <div
                      key={day.value}
                      className={`p-3 rounded-lg border ${
                        isClosed
                          ? "bg-red-50 border-red-200"
                          : "bg-green-50 border-green-200"
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-poppins font-medium text-[15px] text-subtitle">
                          {day.label}
                        </span>
                        <span
                          className={`px-2 py-1 font-nunito text-[14px] font-semibold text-action ${
                            isClosed
                              ? "bg-red-100 text-red-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {isClosed ? "Fermé" : "Ouvert"}
                        </span>
                      </div>
                      {!isClosed && (
                        <div className="space-y-1">
                          <div className="flex items-center gap-1">
                            <FiClock size={12} />
                            {formatTimeForDisplay(
                              currentSalon.openingTime
                            )} -{" "}
                            {formatTimeForDisplay(currentSalon.closingTime)}
                          </div>
                          {hasBreak && (
                            <div className="flex items-center gap-1">
                              <FiPauseCircle size={12} />
                              Pause:{" "}
                              {formatTimeForDisplay(
                                currentSalon.breakStart
                              )} - {formatTimeForDisplay(currentSalon.breakEnd)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Informations de création/mise à jour */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 font-nunito text-[14px] text-text">
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-xl border border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <FiCalendar className="text-blue-600" />
            <span className="font-poppins font-medium text-[15px] text-subtitle">
              Créé le
            </span>
          </div>
          {formatFrenchDateCapitalized(currentSalon.createdAt)}
        </div>
        <div className="bg-gradient-to-r from-gray-50 to-green-50 p-4 rounded-xl border border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <FiEdit3 className="text-green-600" />
            <span className="font-poppins font-medium text-[15px] text-subtitle">
              Dernière modification
            </span>
          </div>
          {formatFrenchDateCapitalized(currentSalon.updatedAt)}
        </div>
      </div>
    </div>
  );
}
